import React, {useEffect, useMemo, useRef, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {Row} from "@tanstack/react-table";
import {VirtualItem} from "@tanstack/react-virtual";
import {AutoEqu, AutoTableInterface, columns as baseColumns} from "@/features/types/AutoTableInterface";
import {ArrowBigLeft, ArrowBigLeftDash, ArrowBigRight, ArrowBigRightDash} from "lucide-react";
import {PaginatorButton} from "@/shared/components/PaginatorButton/PaginatorButton";

interface AutoTableRowDetailsProps {
    row: Row<AutoTableInterface>;
    data: AutoEqu[] | null;
    loading: boolean;
    virtualItem: VirtualItem;
    containerWidth: number;
    outerContainerRef: React.RefObject<HTMLDivElement | null>;
    onDetailsLoaded?: () => void;
}

export default function AutoTableRowDetails({
                                                row,
                                                data,
                                                loading,
                                                virtualItem,
                                                containerWidth,
                                                outerContainerRef,
                                                onDetailsLoaded,
                                            }: AutoTableRowDetailsProps) {
    const detailsFields = [
        "f_supp_2_doc_invc_num",
        "f_supp_2_doc_invc_d",
        "f_supp_2_doc_invc_mnt",
        "f_sales_xls_sales_mnt",
        "f_sales_registr",
        "f_sales_registr_dat",
    ]
    const detailsColumns = baseColumns
        .filter(col => detailsFields.includes(col.accessorKey))
        .map(col => ({accessorKey: col.accessorKey, header: col.header}));

    const [pageIndex, setPageIndex] = useState(0);
    const pageSize = 5;

    const totalPages = Math.ceil((data?.length ?? 0) / pageSize);

    const currentItems = useMemo(
        () => data?.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize) ?? [],
        [data, pageIndex, pageSize]
    );

    const motionDivRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const scrollContainer = outerContainerRef.current;
        const motionDiv = motionDivRef.current;

        if (!scrollContainer || !motionDiv) return;
        motionDiv.style.transform = `translateX(${scrollContainer.scrollLeft}px)`;

        const handleScroll = (e: Event) => {
            window.requestAnimationFrame(() => {
                if (motionDivRef.current) {
                    motionDivRef.current.style.transform = `translateX(${(e.target as HTMLDivElement).scrollLeft}px)`;
                }
            });
        };
        scrollContainer.addEventListener('scroll', handleScroll, {passive: true});

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [outerContainerRef]);

    return (
        <tr
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: virtualItem.size,
                transform: `translateY(${virtualItem.start}px)`,
                width: "100%",
            }}
        >
            <td className="p-0 relative">
                <AnimatePresence initial={false}>
                    <motion.div
                        ref={motionDivRef}
                        initial={{height: 0, opacity: 0}}
                        animate={{height: 240, opacity: 1}}
                        exit={{height: 0, opacity: 0}}
                        transition={{duration: 0.25}}
                        onAnimationComplete={onDetailsLoaded}
                        className="flex gap-4 bg-white py-4 px-10 box-border border-b"
                        style={{
                            width: containerWidth ? `${containerWidth}px` : "100%",
                        }}
                    >
                        <div className="flex flex-col flex-shrink-0 w-[350px] gap-1">
                            {detailsColumns.map(col => {
                                const rawValue = row.original[col.accessorKey as keyof typeof row.original];
                                const value = rawValue !== null && rawValue !== undefined && rawValue !== "" ? rawValue : "-";
                                return (
                                    <div key={col.accessorKey} className="flex">
                                        {/* SZTYWNA szerokość nagłówka zapewnia, że wszystkie wartości startują w tej samej pionowej linii */}
                                        <div className="font-bold text-left w-[160px]">{col.header}:</div>

                                        {/* wartość zajmuje resztę miejsca */}
                                        <div className="flex-1 text-left">{value}</div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="w-[1px] bg-black"></div>

                        <div className="flex-1 flex flex-col h-full">
                            <div className="flex-1 overflow-y-auto">
                                {loading && <div className="italic p-2">Wczytywanie danych…</div>}
                                {!loading && data && data.length === 0 &&
                                  <div className="italic p-2">Brak danych do wyświetlenia</div>}
                                {!loading && data && data.length > 0 && (
                                    <table className="w-full text-left">
                                        <thead>
                                        <tr>
                                            <th className="w-10">Lp.</th>
                                            <th>Opis</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {currentItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="pt-2">{pageIndex * pageSize + idx + 1}.</td>
                                                <td className="pt-2">{item.f_equ_pri_xls_desc_n}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {data && data.length >= 1 && (
                                <div className="flex justify-end items-center mt-2 gap-1">
                                    <PaginatorButton
                                        onClick={() => setPageIndex(0)}
                                        disabled={pageIndex === 0}
                                        icon={<ArrowBigLeftDash className="w-5 h-5"/>}
                                    />
                                    <PaginatorButton
                                        onClick={() => setPageIndex(p => Math.max(p - 1, 0))}
                                        disabled={pageIndex === 0}
                                        icon={<ArrowBigLeft className="w-5 h-5"/>}
                                    />
                                    <span className="px-2 py-1">
                                      {pageIndex + 1} / {totalPages}
                                    </span>
                                    <PaginatorButton
                                        onClick={() => setPageIndex(p => Math.min(p + 1, totalPages - 1))}
                                        disabled={pageIndex >= totalPages - 1}
                                        icon={<ArrowBigRight className="w-5 h-5"/>}
                                    />
                                    <PaginatorButton
                                        onClick={() => setPageIndex(totalPages - 1)}
                                        disabled={pageIndex >= totalPages - 1}
                                        icon={<ArrowBigRightDash className="w-5 h-5"/>}
                                    />
                                </div>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </td>
        </tr>
    );
}
